import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, Mic, Square, Loader2, Trash2, Volume2, TrendingUp, Award, Calendar, BarChart3 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const PROMPTS = [
  { de: 'Guten Morgen! Wie geht es Ihnen heute?', en: 'Good morning! How are you today?', level: 'A1' },
  { de: 'Ich möchte einen Termin beim Arzt vereinbaren.', en: 'I would like to make a doctor\'s appointment.', level: 'A2' },
  { de: 'Können Sie mir bitte den Weg zum Bahnhof erklären?', en: 'Can you explain the way to the station?', level: 'A2' },
  { de: 'Mein Großvater war Bäcker. Er stand jeden Morgen um drei Uhr auf.', en: 'My grandfather was a baker. He got up at 3am every morning.', level: 'A2' },
  { de: 'Ich würde gern eine Geburtstagsparty machen.', en: 'I would like to have a birthday party.', level: 'A2' },
  { de: 'Stell die Kommode bitte an die Wand neben die Tür.', en: 'Put the dresser against the wall next to the door.', level: 'A2' },
  { de: 'Der Patient hat seit drei Tagen Fieber und Husten.', en: 'The patient has had fever and cough for three days.', level: 'Pflege' },
  { de: 'Bitte messen Sie den Blutdruck und die Temperatur.', en: 'Please measure the blood pressure and temperature.', level: 'Pflege' },
  { de: 'Die Medikamente müssen dreimal täglich eingenommen werden.', en: 'The medication must be taken three times daily.', level: 'Pflege' },
  { de: 'Ich bin so aufgeregt, weil ich die Prüfung bestanden habe!', en: 'I\'m so excited because I passed the exam!', level: 'A2' },
  { de: 'Herzlichen Glückwunsch zur bestandenen Prüfung!', en: 'Congratulations on passing the exam!', level: 'A2' },
  { de: 'In Deutschland muss man den Müll trennen.', en: 'In Germany you must separate waste.', level: 'A2' },
];

export default function VoiceDiary() {
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [currentPrompt, setCurrentPrompt] = useState(null);
  const [customText, setCustomText] = useState('');
  const [useCustom, setUseCustom] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [tab, setTab] = useState('record');
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const promptRef = useRef(null);
  const customRef = useRef({ useCustom: false, text: '' });

  useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [entriesRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/voice-diary/entries`, { withCredentials: true }),
        axios.get(`${API}/api/voice-diary/stats`, { withCredentials: true }),
      ]);
      setEntries(entriesRes.data.entries || []);
      setStats(statsRes.data);
    } catch (err) { toast.error('Failed to load diary data'); }
    finally { setLoading(false); }
  };

  const pickRandomPrompt = () => {
    const p = PROMPTS[Math.floor(Math.random() * PROMPTS.length)];
    setCurrentPrompt(p);
    promptRef.current = p;
    setUseCustom(false);
    customRef.current = { useCustom: false, text: '' };
    setLastResult(null);
  };

  useEffect(() => { pickRandomPrompt(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
      streamRef.current?.getTracks().forEach(t => t.stop());
      setRecording(false);
    }
  }, []);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      mediaRecorderRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => handleRecordingComplete();
      mr.start(100);
      setRecording(true);
      setTimeout(() => { if (mediaRecorderRef.current?.state === 'recording') stopRecording(); }, 30000);
    } catch { toast.error('Microphone access denied'); }
  }, [stopRecording]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRecordingComplete = async () => {
    const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
    if (blob.size < 100) { toast.error('Recording too short'); return; }
    setProcessing(true);
    const isCustom = customRef.current.useCustom;
    const text = isCustom ? customRef.current.text : promptRef.current?.de;
    if (!text) { setProcessing(false); return; }

    const formData = new FormData();
    formData.append('file', blob, 'voice_diary.webm');
    formData.append('expected_text', text);
    formData.append('lesson_ref', promptRef.current?.level || 'custom');

    try {
      const res = await axios.post(`${API}/api/voice-diary/record`, formData, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
      });
      setLastResult(res.data);
      setEntries(prev => [res.data, ...prev]);
      setStats(prev => prev ? { ...prev, total_entries: (prev.total_entries || 0) + 1 } : prev);
      toast.success(`Score: ${res.data.overall_score}%`);
    } catch (e) { toast.error('Recording failed. Try again.'); }
    finally { setProcessing(false); }
  };

  const playTTS = async (text) => {
    try {
      const res = await axios.post(`${API}/api/speech/synthesize`, { text, voice: 'nova' }, {
        withCredentials: true
      });
      if (res.data.audio) {
        const audio = new Audio(`data:audio/mp3;base64,${res.data.audio}`);
        audio.play();
      }
    } catch { toast.error('Audio playback failed'); }
  };

  const deleteEntry = async (entryId) => {
    try {
      await axios.delete(`${API}/api/voice-diary/entries/${entryId}`, { withCredentials: true });
      setEntries(prev => prev.filter(e => e.entry_id !== entryId));
      toast.success('Entry deleted');
    } catch { toast.error('Delete failed'); }
  };

  const scoreColor = (s) => s >= 80 ? '#22c55e' : s >= 60 ? '#f59e0b' : '#ef4444';

  if (loading) return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-900 text-white" data-testid="voice-diary-page">
      <style>{`
        .vd-container { max-width: 800px; margin: 0 auto; padding: 20px; }
        .score-ring { position: relative; width: 120px; height: 120px; margin: 0 auto; }
        .score-ring svg { transform: rotate(-90deg); }
        .score-ring .score-text { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; font-size: 28px; font-weight: 800; }
        .word-chip { display: inline-flex; padding: 4px 10px; border-radius: 6px; font-size: 13px; margin: 3px; font-weight: 500; }
        .word-correct { background: rgba(34,197,94,0.15); color: #22c55e; border: 1px solid rgba(34,197,94,0.3); }
        .word-wrong { background: rgba(239,68,68,0.15); color: #ef4444; border: 1px solid rgba(239,68,68,0.3); text-decoration: line-through; }
        .history-bar { height: 6px; border-radius: 3px; transition: all 0.3s; }
        .prompt-card { background: linear-gradient(135deg, #1e293b, #0f172a); border: 2px solid #334155; border-radius: 16px; padding: 24px; text-align: center; margin-bottom: 24px; transition: border-color 0.3s; }
        .prompt-card:hover { border-color: #10b981; }
        .prompt-text { font-size: 22px; font-weight: 700; color: #f1f5f9; line-height: 1.4; margin-bottom: 8px; }
        .prompt-en { font-size: 14px; color: #94a3b8; }
        .record-btn { width: 80px; height: 80px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: none; cursor: pointer; transition: all 0.2s; margin: 0 auto; }
        .record-btn.idle { background: linear-gradient(135deg, #10b981, #059669); box-shadow: 0 4px 20px rgba(16,185,129,0.4); }
        .record-btn.idle:hover { transform: scale(1.05); box-shadow: 0 6px 30px rgba(16,185,129,0.5); }
        .record-btn.active { background: #ef4444; animation: pulse-record 1.5s infinite; }
        @keyframes pulse-record { 0%,100% { box-shadow: 0 0 0 0 rgba(239,68,68,0.5); } 50% { box-shadow: 0 0 0 16px rgba(239,68,68,0); } }
        .result-card { background: linear-gradient(135deg, #1e293b, #0f172a); border: 2px solid #10b981; border-radius: 16px; padding: 24px; margin-top: 24px; }
        .feedback-box { background: rgba(16,185,129,0.08); border: 1px solid rgba(16,185,129,0.2); border-radius: 12px; padding: 16px; margin-top: 16px; white-space: pre-wrap; font-size: 14px; line-height: 1.6; color: #e2e8f0; }
        .entry-card { background: #1e293b; border: 1px solid #334155; border-radius: 12px; padding: 16px; margin-bottom: 12px; transition: all 0.2s; }
        .entry-card:hover { border-color: #475569; }
        .stat-card { background: linear-gradient(135deg, #1e293b, #0f172a); border: 1px solid #334155; border-radius: 12px; padding: 20px; text-align: center; }
        .stat-value { font-size: 32px; font-weight: 800; }
        .stat-label { font-size: 12px; color: #94a3b8; margin-top: 4px; }
        .tab-btn { padding: 10px 20px; border-radius: 8px; font-weight: 600; font-size: 14px; cursor: pointer; transition: all 0.2s; border: 1px solid transparent; background: transparent; color: #94a3b8; }
        .tab-btn:hover { background: #1e293b; }
        .tab-btn.active { background: #10b981; color: white; border-color: #10b981; }
      `}</style>

      <div className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur border-b border-slate-700">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-slate-400 hover:text-white" data-testid="back-link">
            <ChevronLeft className="h-5 w-5" /><span className="text-sm">Back</span>
          </Link>
          <h1 className="text-emerald-500 font-bold">Sprachaufnahme-Tagebuch</h1>
          <div className="w-20" />
        </div>
      </div>

      <div className="vd-container">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold mb-1" style={{ color: '#10b981' }}>Sprachaufnahme-Tagebuch</h1>
          <p className="text-slate-400 text-sm">Voice Recording Diary · Record, score, improve your German pronunciation</p>
        </div>

        {/* Stats Row */}
        {stats && (
          <div className="grid grid-cols-4 gap-3 mb-6" data-testid="stats-row">
            <div className="stat-card">
              <div className="stat-value" style={{ color: '#10b981' }}>{stats.total_entries}</div>
              <div className="stat-label">Recordings</div>
            </div>
            <div className="stat-card">
              <div className="stat-value" style={{ color: '#3b82f6' }}>{stats.avg_score}%</div>
              <div className="stat-label">Avg Score</div>
            </div>
            <div className="stat-card">
              <div className="stat-value" style={{ color: '#f59e0b' }}>{stats.best_score}%</div>
              <div className="stat-label">Best Score</div>
            </div>
            <div className="stat-card">
              <div className="stat-value" style={{ color: '#a78bfa' }}>{stats.streak}</div>
              <div className="stat-label">Day Streak</div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 justify-center" data-testid="diary-tabs">
          <button className={`tab-btn ${tab === 'record' ? 'active' : ''}`} onClick={() => setTab('record')} data-testid="tab-record">
            <Mic className="h-4 w-4 inline mr-1" /> Record
          </button>
          <button className={`tab-btn ${tab === 'history' ? 'active' : ''}`} onClick={() => setTab('history')} data-testid="tab-history">
            <Calendar className="h-4 w-4 inline mr-1" /> History ({entries.length})
          </button>
        </div>

        {/* Record Tab */}
        {tab === 'record' && (
          <div data-testid="record-section">
            {/* Prompt Card */}
            <div className="prompt-card" data-testid="prompt-card">
              {!useCustom && currentPrompt && (
                <>
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <span className="px-2 py-0.5 rounded text-xs font-bold" style={{ background: 'rgba(16,185,129,0.2)', color: '#10b981' }}>{currentPrompt.level}</span>
                  </div>
                  <div className="prompt-text">{currentPrompt.de}</div>
                  <div className="prompt-en">{currentPrompt.en}</div>
                  <div className="flex items-center justify-center gap-2 mt-4">
                    <Button variant="outline" size="sm" onClick={() => playTTS(currentPrompt.de)} data-testid="listen-btn">
                      <Volume2 className="h-4 w-4 mr-1" /> Listen
                    </Button>
                    <Button variant="outline" size="sm" onClick={pickRandomPrompt} data-testid="new-prompt-btn">
                      New Prompt
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => { setUseCustom(true); customRef.current = { useCustom: true, text: customText }; }} className="text-slate-400">
                      Custom text
                    </Button>
                  </div>
                </>
              )}
              {useCustom && (
                <>
                  <p className="text-sm text-slate-400 mb-3">Type your own German text to practice:</p>
                  <textarea value={customText} onChange={e => { setCustomText(e.target.value); customRef.current = { useCustom: true, text: e.target.value }; }}
                    className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 text-white text-lg mb-3 resize-none"
                    rows={2} placeholder="z.B. Guten Tag, ich heiße..." data-testid="custom-text-input" />
                  <Button variant="ghost" size="sm" onClick={() => setUseCustom(false)} className="text-slate-400">
                    Use prompts instead
                  </Button>
                </>
              )}
            </div>

            {/* Record Button */}
            <div className="text-center mb-6">
              {processing ? (
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="h-12 w-12 animate-spin text-emerald-500" />
                  <p className="text-sm text-slate-400">Analyzing pronunciation...</p>
                </div>
              ) : (
                <>
                  <button
                    className={`record-btn ${recording ? 'active' : 'idle'}`}
                    onClick={recording ? stopRecording : startRecording}
                    disabled={useCustom && !customText.trim()}
                    data-testid="record-button"
                  >
                    {recording ? <Square className="h-8 w-8 text-white" /> : <Mic className="h-8 w-8 text-white" />}
                  </button>
                  <p className="text-xs text-slate-500 mt-3">{recording ? 'Tap to stop recording' : 'Tap to start recording'}</p>
                </>
              )}
            </div>

            {/* Result Card */}
            {lastResult && (
              <div className="result-card" data-testid="result-card">
                <div className="flex items-start gap-6">
                  {/* Score Ring */}
                  <div className="score-ring flex-shrink-0">
                    <svg width="120" height="120" viewBox="0 0 120 120">
                      <circle cx="60" cy="60" r="52" fill="none" stroke="#1e293b" strokeWidth="8" />
                      <circle cx="60" cy="60" r="52" fill="none" stroke={scoreColor(lastResult.overall_score)}
                        strokeWidth="8" strokeLinecap="round"
                        strokeDasharray={`${(lastResult.overall_score / 100) * 327} 327`} />
                    </svg>
                    <div className="score-text" style={{ color: scoreColor(lastResult.overall_score) }}>
                      {lastResult.overall_score}%
                    </div>
                  </div>
                  {/* Details */}
                  <div className="flex-1">
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      <div><p className="text-xs text-slate-500">Accuracy</p><p className="text-lg font-bold" style={{ color: scoreColor(lastResult.accuracy) }}>{lastResult.accuracy}%</p></div>
                      <div><p className="text-xs text-slate-500">Fluency</p><p className="text-lg font-bold" style={{ color: scoreColor(lastResult.fluency) }}>{lastResult.fluency}%</p></div>
                      <div><p className="text-xs text-slate-500">Completeness</p><p className="text-lg font-bold" style={{ color: scoreColor(lastResult.completeness) }}>{lastResult.completeness}%</p></div>
                    </div>
                    <div className="mb-2">
                      <p className="text-xs text-slate-500 mb-1">You said:</p>
                      <p className="text-sm text-slate-300 italic">"{lastResult.transcribed_text}"</p>
                    </div>
                    {lastResult.word_details && (
                      <div className="flex flex-wrap mt-2">
                        {lastResult.word_details.map((w, i) => (
                          <span key={`wd-${w.expected}-${i}`} className={`word-chip ${w.correct ? 'word-correct' : 'word-wrong'}`}>
                            {w.expected} {!w.correct && w.spoken ? `→ ${w.spoken}` : ''}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                {lastResult.feedback && (
                  <div className="feedback-box" data-testid="ai-feedback">{lastResult.feedback}</div>
                )}
                <div className="flex justify-center mt-4">
                  <Button variant="outline" size="sm" onClick={pickRandomPrompt} data-testid="try-another-btn">
                    Try Another
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* History Tab */}
        {tab === 'history' && (
          <div data-testid="history-section">
            {/* Mini chart */}
            {stats?.history?.length > 1 && (
              <Card className="bg-slate-800/50 border-slate-700 mb-4">
                <CardContent className="p-4">
                  <p className="text-xs text-slate-400 mb-2 flex items-center gap-1"><BarChart3 className="h-3 w-3" /> Score Trend (Last 30)</p>
                  <div className="flex items-end gap-1 h-16">
                    {stats.history.map((h, i) => (
                      <div key={`hist-${h.date}-${i}`} className="flex-1 flex flex-col items-center justify-end">
                        <div className="history-bar w-full" style={{ height: `${Math.max(4, (h.score / 100) * 64)}px`, background: scoreColor(h.score) }} title={`${h.score}%`} />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {entries.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Mic className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>No recordings yet. Start practicing!</p>
              </div>
            ) : (
              entries.map(e => (
                <div key={e.entry_id} className="entry-card" data-testid={`entry-${e.entry_id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl font-bold" style={{ color: scoreColor(e.overall_score) }}>{e.overall_score}%</span>
                      {e.lesson_ref && <span className="px-2 py-0.5 rounded text-xs bg-slate-700 text-slate-400">{e.lesson_ref}</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500">{new Date(e.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                      <button onClick={() => deleteEntry(e.entry_id)} className="text-slate-600 hover:text-red-400 transition-colors" data-testid={`delete-${e.entry_id}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <p className="text-sm text-slate-300 mb-1"><strong>Expected:</strong> {e.expected_text}</p>
                  <p className="text-sm text-slate-400 italic"><strong>You said:</strong> "{e.transcribed_text}"</p>
                  <div className="flex gap-4 mt-2 text-xs text-slate-500">
                    <span>Accuracy: {e.accuracy}%</span>
                    <span>Fluency: {e.fluency}%</span>
                    <span>Completeness: {e.completeness}%</span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
